/*
 * Set quantity in the cart (merchant, Nov 3 2026). Markup: the "+ 1 -" control in snippets/cart-build-box-group.liquid.
 *
 * Each copy of a set stays its own group ("Set N"), so every copy keeps its own Gift Options, card and message.
 * The number = how many identical copies (same spices in the same slots, same box or no box) are in this cart view.
 * +  adds one more copy as a new group: same jars and box, never the card or message.
 * -  removes the newest identical copy that has no card or message. When every copy has one, it opens the trash
 *    question on the newest copy instead, so a written message is never dropped without asking.
 */
(() => {
  if (window.__wzSetQty) return;
  window.__wzSetQty = true;
  const base = window.Shopify?.routes?.root || '/';
  const getCart = async () => (await fetch(`${base}cart.js`, { credentials: 'same-origin' })).json();
  const post = async (url, body) => {
    const res = await fetch(base + url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.description || data.message || 'Cart error');
    return data;
  };
  const refresh = async () => {
    try {
      await customElements.whenDefined('s-cart');
      await customElements.get('s-cart')?.refreshMountedCarts?.();
    } catch (e) { /* next page load shows it */ }
  };
  const newId = () => Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
  const nameOf = (it) => String(it.properties?.['Custom Box'] || '').split(' · ')[0];
  // Drawer and cart page each count their own rows.
  const scopeOf = (el) => el.closest('dialog') || document.body;
  const copiesOf = (qty) => [...scopeOf(qty).querySelectorAll('[data-bb-qty]')]
    .filter((q) => q.dataset.bbQty === qty.dataset.bbQty && scopeOf(q) === scopeOf(qty));

  function paint() {
    document.querySelectorAll('[data-bb-qty]').forEach((q) => {
      const num = q.querySelector('[data-bb-qty-num]');
      const n = String(copiesOf(q).length);
      if (num && num.textContent !== n) num.textContent = n;
    });
  }

  function say(qty, text) {
    const msg = qty.closest('[data-bb-group]')?.querySelector('[data-bb-qty-msg]');
    if (!msg) return;
    msg.textContent = text;
    msg.hidden = !text;
  }

  async function addCopy(qty) {
    const bb = qty.dataset.bbId;
    const items = (await getCart()).items || [];
    const lines = items.filter((i) => i.properties?._bb === bb);
    if (!lines.length) return;
    // Next free "Set N" name, the same way new sets are named (assets/set-edit.js).
    const names = new Set(items.map(nameOf).filter(Boolean));
    let no = new Set(items.map((i) => i.properties?._bb).filter(Boolean)).size + 1;
    while (names.has(`Set ${no}`)) no++;
    const name = `Set ${no}`;
    const id = newId();
    const add = lines
      .sort((a, b) => Number(a.properties?._slot || 0) - Number(b.properties?._slot || 0))
      .map((line) => {
        const props = { ...line.properties, _bb: id };
        const label = String(props['Custom Box'] || '');
        props['Custom Box'] = label.includes(' · ') ? `${name} · ${label.split(' · ').slice(1).join(' · ')}` : name;
        return { id: line.variant_id, quantity: 1, properties: props };
      });
    // The copy starts hidden so the cart does not grow by a whole set list (merchant, Nov 3 2026).
    window.WZ_hideSet?.(id);
    await post('cart/add.js', { items: add });
  }

  async function removeCopy(qty) {
    const copies = copiesOf(qty);
    const newestFirst = copies.sort((a, b) => Number(b.dataset.setNo || 0) - Number(a.dataset.setNo || 0));
    const plain = newestFirst.find((q) => !q.hasAttribute('data-has-card'));
    if (!plain) {
      // Every copy has a card or message: ask on the newest copy, like its trash does.
      const row = newestFirst[0].closest('[data-bb-group]');
      row?.querySelector('[data-bb-group-remove]')?.click();
      row?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      return false;
    }
    if (copies.length === 1) {
      // Last copy: same as the trash (asks first).
      plain.closest('[data-bb-group]')?.querySelector('[data-bb-group-remove]')?.click();
      return false;
    }
    const keys = (plain.dataset.keys || '').split(',').filter(Boolean);
    if (!keys.length) return false;
    const updates = {};
    keys.forEach((k) => { updates[k] = 0; });
    await post('cart/update.js', { updates });
    return true;
  }

  document.addEventListener('click', async (e) => {
    const btn = e.target instanceof Element ? e.target.closest('[data-bb-qty-step]') : null;
    if (!btn) return;
    e.preventDefault();
    const qty = btn.closest('[data-bb-qty]');
    if (!qty || qty.dataset.busy) return;
    say(qty, '');
    qty.dataset.busy = '1';
    qty.querySelectorAll('button').forEach((b) => { b.disabled = true; });
    try {
      const changed = btn.dataset.bbQtyStep === '1' ? (await addCopy(qty), true) : await removeCopy(qty);
      if (changed) await refresh();
    } catch (err) {
      // Usually stock: Shopify says which spice can't be added again.
      say(qty, err.message);
    } finally {
      delete qty.dataset.busy;
      qty.querySelectorAll('button').forEach((b) => { b.disabled = false; });
      paint();
    }
  });

  // Cart re-renders replace the rows: recount after every change.
  let queued = false;
  new MutationObserver(() => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => { queued = false; paint(); });
  }).observe(document.documentElement, { childList: true, subtree: true });
  paint();
})();
